import React, { useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { Box, Button, Divider } from '@mui/material';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FormObject } from '@europrocurement/flexy-form/components/FlexyForm/FlexyFormTypes';
import {
    EMAIL_VALIDATION_REGEX,
    PHONE_NUMBER_VALIDATION_REGEX,
} from '@europrocurement/l2d-modules/validation';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { usePrescripteurForm } from '../../providers/PrescripteurFormContext';
import { crmPrescripteurDetail } from '../../constants/paths';
import usePrescriberCRM from '../../hooks/usePrescriberCRM';
import { CIVILITE_OPTIONS, ContactFormData } from './contactTypes';

type FormValues = {
    contacts: ContactFormData[];
};

const DEFAULT_CONTACT: ContactFormData = {
    civilite: CIVILITE_OPTIONS[0],
    nomContact: '',
    prenomContact: '',
    email: '',
    telephone: '',
};

const CONTACT_SUBFORM_STRUCTURE: FormStructure[] = [
    {
        type: 'select',
        name: 'civilite',
        inputlabel: 'Civilité',
        options: CIVILITE_OPTIONS,
        xs: 12,
        sm: 12,
        lg: 12,
        rules: { required: 'La civilité est obligatoire' },
    },
    {
        type: 'text',
        name: 'nomContact',
        inputlabel: 'Nom',
        xs: 12,
        sm: 6,
        lg: 6,
        rules: { required: 'Le nom est obligatoire' },
    },
    {
        type: 'text',
        name: 'prenomContact',
        inputlabel: 'Prénom',
        xs: 12,
        sm: 6,
        lg: 6,
        rules: { required: 'Le prénom est obligatoire' },
    },
    {
        type: 'email',
        name: 'email',
        inputlabel: 'Email',
        xs: 12,
        sm: 6,
        lg: 6,
        rules: {
            required: "L'adresse email est obligatoire",
            pattern: {
                value: EMAIL_VALIDATION_REGEX,
                message: "L'email est invalide",
            },
        },
    },
    {
        type: 'text',
        name: 'telephone',
        inputlabel: 'Téléphone',
        xs: 12,
        sm: 6,
        lg: 6,
        rules: {
            pattern: {
                value: PHONE_NUMBER_VALIDATION_REGEX,
                message: 'Le numéro de téléphone est invalide',
            },
        },
    },
];

const CONTACTS_FORM_STRUCTURE: FormStructure[] = [
    {
        type: 'subformarray',
        name: 'contacts',
        minLength: 1,
        displayAddButton: true,
        displayClearButton: true,
        addBtnLabel: 'Ajouter un contact',
        removeBtnTooltip: 'Supprimer ce contact',
        subFormHeader: (_input, _object, index) =>
            index > 0 ? <Divider sx={{ mt: 1, mb: 3, width: '100%' }} /> : null,
        events: {
            beforeAppend: () => ({ item: { ...DEFAULT_CONTACT } }),
        },
        structure: CONTACT_SUBFORM_STRUCTURE,
        xs: 12,
        sm: 12,
        lg: 12,
    },
];

type StepContactsProps = {
    onBack: () => void;
};

const StepContacts: React.FunctionComponent<StepContactsProps> = function ({ onBack }) {
    const { step1, step2, step3, setStep3 } = usePrescripteurForm();
    const { createPrescripteur } = usePrescriberCRM();
    const navigate = useNavigate();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const savedContacts = step3?.contacts as ContactFormData[] | undefined;

    const defaultContacts = (() => {
        if (savedContacts) return savedContacts;
        if (step1?.personneType === 'physique') {
            return [{ ...DEFAULT_CONTACT, nomContact: step1.nom, prenomContact: step1.prenom }];
        }
        return [{ ...DEFAULT_CONTACT }];
    })();

    const methods = useForm<FormValues>({
        mode: 'onTouched',
        defaultValues: {
            contacts: defaultContacts,
        },
    });

    const saveToContext = () => {
        const { contacts } = methods.getValues();
        setStep3({ contacts });
    };

    const onSubmit = async (values: FormValues) => {
        if (!step1 || !step2) {
            enqueueSnackbar('Données incomplètes. Veuillez compléter les étapes précédentes.', {
                variant: 'error',
            });
            return;
        }

        setIsSubmitting(true);
        const step3Data = { contacts: values.contacts };
        setStep3({ contacts: values.contacts });

        try {
            const prescriberId = await createPrescripteur(step1, step2, step3Data);
            if (prescriberId) {
                navigate(`${crmPrescripteurDetail}/${prescriberId}`);
            }
        } catch (error: unknown) {
            console.error(error);
            enqueueSnackbar('Une erreur est survenue lors de la création du prescripteur.', {
                variant: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        saveToContext();
        onBack();
    };

    return (
        <Box>
            <FlexyForm
                formContext={methods as unknown as UseFormReturn<FormObject>}
                formStructure={CONTACTS_FORM_STRUCTURE}
                onSubmit={onSubmit as unknown as (data: FormObject) => void}
                submitButton={{ displayed: false }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={isSubmitting}
                    sx={{ padding: '16px 24px' }}
                >
                    Précédent
                </Button>
                <Button
                    variant="contained"
                    onClick={methods.handleSubmit(onSubmit)}
                    disabled={isSubmitting}
                    sx={{ padding: '16px 24px' }}
                >
                    {isSubmitting ? 'Création en cours...' : 'Créer le prescripteur'}
                </Button>
            </Box>
        </Box>
    );
};

export default StepContacts;
